import React, {useCallback} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {AvailableTranslationKeys} from '../../../../locale/types'
import {useLocale} from '../../../../utils/i18n'
import {useModalActions} from '../../../../utils/modals'


const UnavailableTimeContent = (_: {primaryActionKey: AvailableTranslationKeys}) => {
  const {l} = useLocale()
  return (
    <div>
      <p>{l('selected_datetime_not_available')}</p>
      <div style={{marginTop: 8, color: 'var(--text-secondary)'}}>
        <p>
          {l('timeslot_unavailable_explanation')}
        </p>
      </div>
    </div>
  )
}

type validationSettingType = {
  redirectPath: string
  title: AvailableTranslationKeys
  submitButton: AvailableTranslationKeys
}

type validationSettingsMapType = {
  class: validationSettingType
  service: validationSettingType
}

const getServiceRedirectPath = (entityId: string, currentPathname: string): string => {
  try {
    const segments = currentPathname.split('/').filter((el) => el !== '')
    const venueId = segments[0]
    const section = segments[1]
    const allowedSections = ['service', 'space', 'instructor', 'instructor-service']
    if (venueId && section && allowedSections.includes(section)) {
      return `/${venueId}/${section}/${entityId}/select-date-time`
    }
  } catch (_err) {
    // no-op, fallback below
  }
  return '../select-date-time'
}

const validationSettingsMap: (entityId: string, currentPathname: string) => validationSettingsMapType = (entityId, currentPathname) => {
  const serviceRedirectPath = getServiceRedirectPath(entityId, currentPathname)

  return {
    class: {
      submitButton: 'select_sessions',
      title: 'sessions_unavailable_single',
      redirectPath: `../class/${entityId}/select-session`,
    },
    service: {
      submitButton: 'select_timeslot',
      title: 'timeslot_not_available',
      redirectPath: serviceRedirectPath,
    },
  }
}

export const useOpenBookingValidationModal = () => {
  const {openModal, closeAllModals} = useModalActions()
  const navigate = useNavigate()
  const {l} = useLocale()
  const {pathname} = useLocation()
  const pathArr = pathname.split('/').filter((el) => el !== '')

  const openBookingValidationModal = useCallback(({type}: { type: 'class' | 'service' }) => {
    const entityIdFromPath = pathArr[pathArr.length - 2]
    try {
      const map = validationSettingsMap(entityIdFromPath, pathname)
      const settings: validationSettingType = map[type]
      openModal({
        title: l(settings.title),
        settings: {
          footer: {
            submitButton: {
              settings: {
                text: {
                  width: 'max-content',
                }
              },
            },
          },
        },
        component: <UnavailableTimeContent primaryActionKey={settings.submitButton} />,
        footerConfig: {
          submitButton: {
            text: l(settings.submitButton),
            onClick: () => {
              navigate(settings.redirectPath, {state: {clearTime: true}})
              closeAllModals()
            },
          },
          cancelButton: {
            text: l('home'),
            settings: {
              width: 'max-content',
            },
            onClick: () => {
              const venueIdFromPath = pathArr[0]
              navigate(`/${venueIdFromPath}/`)
              closeAllModals()
            },
          },
        },
      })
    } catch (err) {
      console.error('====> ~ openBookingValidationModal ~ ERROR invoking modal:', err)
    }
  }, [openModal, closeAllModals, navigate, l, pathArr])

  return {
    openBookingValidationModal,
  }
}
